import React from 'react';
import {
    GasBadge,
    GasBannerAlert,
    GasButton,
    GasEmptyState,
    useGasI18n,
    useGasNotify,
} from '../../shared/kit';
import {
    addToCartFromSearch,
    revalidateVariant,
    searchApply,
    searchNatural,
    trackSearchEvent,
    trackSearchRun,
} from './api';
import {
    chipsToFilters,
    hasExactVariant,
    removeChipAt,
    setAvailabilityChip,
    setPriceLteChip,
    variantIdOf,
} from './filters';
import type {
    AvailabilityBucket,
    ShopSearchCard,
    ShopSearchCatalogInitData,
    ShopSearchChip,
    ShopSearchResponse,
    SearchUiStatus,
} from './types';

interface AppProps {
    initData: ShopSearchCatalogInitData;
}

function bucketTone(bucket: AvailabilityBucket | undefined): 'success' | 'warning' | 'danger' | 'muted' | 'default' {
    switch (bucket) {
        case 'in_stock':
            return 'success';
        case 'low_stock':
            return 'warning';
        case 'backorder':
            return 'default';
        case 'unavailable':
            return 'danger';
        default:
            return 'muted';
    }
}

function formatPrice(amount: number | null | undefined, currency = 'CHF', locale = 'fr-CH'): string {
    if (amount == null || Number.isNaN(Number(amount))) {
        return '—';
    }
    return new Intl.NumberFormat(locale.replace('_', '-'), {
        style: 'currency',
        currency: currency || 'CHF',
        minimumFractionDigits: 2,
    }).format(Number(amount));
}

function optionLabel(keys: Record<string, string> | undefined): string {
    if (!keys || Object.keys(keys).length === 0) {
        return '';
    }
    return Object.entries(keys)
        .map(([k, v]) => `${k}=${v}`)
        .join(' · ');
}

export default function App({ initData }: AppProps): React.ReactElement {
    const { t } = useGasI18n();
    const notify = useGasNotify();

    const [query, setQuery] = React.useState('');
    const [chips, setChips] = React.useState<ShopSearchChip[]>([]);
    const [response, setResponse] = React.useState<ShopSearchResponse | null>(null);
    const [status, setStatus] = React.useState<SearchUiStatus>('idle');
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [addingId, setAddingId] = React.useState<number | null>(null);
    const [unavailableIds, setUnavailableIds] = React.useState<Set<number>>(new Set());
    const [sessionToken, setSessionToken] = React.useState<string | null>(null);
    const [priceCap, setPriceCap] = React.useState<string>('');
    const [availability, setAvailability] = React.useState<string>('');

    const requestSeq = React.useRef(0);
    const telemetryRunRef = React.useRef<Promise<unknown> | null>(null);
    const examples = initData.examples?.length
        ? initData.examples
        : [t('shop.search.examples.tshirt'), t('shop.search.examples.bag')];

    const applyResponse = React.useCallback((next: ShopSearchResponse, startedAt: number, rawQuery: string) => {
        setResponse(next);
        setUnavailableIds(new Set());
        const nextChips = Array.isArray(next.interpretation?.chips) ? next.interpretation!.chips! : [];
        setChips(nextChips);

        if (next.status === 'denied') {
            setStatus('denied');
            setErrorMessage(next.deny_reason || t('shop.search.errors.denied'));
            return;
        }

        const results = Array.isArray(next.results) ? next.results : [];
        setStatus(results.length === 0 ? 'empty' : 'ready');
        setErrorMessage(null);

        const impressions = results.slice(0, 20).map((card, rank) => ({
            object_type: 'shop_product',
            object_id: card.shop_product_id,
            variant_id: variantIdOf(card) || null,
            rank: rank + 1,
        }));

        const telemetryRun = trackSearchRun(initData, {
            query_id: next.query_id,
            query: rawQuery,
            filters: next.interpretation?.filters,
            result_count: results.length,
            latency_ms: Math.max(0, Date.now() - startedAt),
            impressions,
            session_token: sessionToken,
        }).then((telemetry) => {
            if (telemetry?.session_token) {
                setSessionToken(String(telemetry.session_token));
            }
        });
        telemetryRunRef.current = telemetryRun;
        void telemetryRun;
    }, [initData, sessionToken, t]);

    const runNaturalSearch = React.useCallback(async (text: string) => {
        const normalized = text.trim();
        if (normalized.length < 2) {
            requestSeq.current += 1;
            setStatus('idle');
            setResponse(null);
            setChips([]);
            setErrorMessage(null);
            return;
        }

        const seq = ++requestSeq.current;
        const startedAt = Date.now();
        setStatus('loading');
        setErrorMessage(null);

        try {
            const next = await searchNatural(initData, normalized);
            if (seq !== requestSeq.current) return;
            applyResponse(next, startedAt, normalized);
        } catch (err) {
            if (seq !== requestSeq.current) return;
            setStatus('error');
            setResponse(null);
            setErrorMessage(err instanceof Error ? err.message : t('shop.search.errors.generic'));
        }
    }, [applyResponse, initData, t]);

    const runApply = React.useCallback(async (nextChips: ShopSearchChip[], residual = '') => {
        const seq = ++requestSeq.current;
        const startedAt = Date.now();
        setStatus('loading');
        setErrorMessage(null);

        try {
            const filters = chipsToFilters(nextChips);
            const previousQueryId = response?.query_id;
            const previousTelemetryRun = telemetryRunRef.current;
            const next = await searchApply(initData, filters, nextChips, {
                residual,
            });
            if (seq !== requestSeq.current) return;
            setChips(nextChips);
            applyResponse(next, startedAt, residual || query);
            if (previousQueryId) {
                // Le run précédent doit exister avant son événement. Sans
                // cet ordre, le backend fail-closed peut refuser un changement
                // de filtre déclenché très vite après le premier résultat.
                void (previousTelemetryRun ?? Promise.resolve()).then(() => trackSearchEvent(initData, {
                    query_id: previousQueryId,
                    event_type: 'filter_change',
                    session_token: sessionToken,
                }));
            }
        } catch (err) {
            if (seq !== requestSeq.current) return;
            setStatus('error');
            setErrorMessage(err instanceof Error ? err.message : t('shop.search.errors.generic'));
        }
    }, [applyResponse, initData, query, response?.query_id, sessionToken, t]);

    React.useEffect(() => {
        const handle = window.setTimeout(() => {
            // Recherche NL seulement si pas de chips manuelles en cours d'édition pure.
            // Debounce 350 ms.
            void runNaturalSearch(query);
        }, 350);
        return () => window.clearTimeout(handle);
    }, [query]); // eslint-disable-line react-hooks/exhaustive-deps -- debounce on query only

    function onRemoveChip(index: number): void {
        const next = removeChipAt(chips, index);
        void runApply(next, response?.interpretation?.residual || query);
    }

    function onAvailabilityChange(value: string): void {
        setAvailability(value);
        const next = setAvailabilityChip(
            chips,
            value || null,
            value ? t(`shop.search.buckets.${value}`) : undefined,
        );
        void runApply(next, response?.interpretation?.residual || query);
    }

    function onPriceCapApply(): void {
        const amount = priceCap.trim() === '' ? null : Number(priceCap.replace(',', '.'));
        const next = setPriceLteChip(
            chips,
            amount !== null && Number.isFinite(amount) ? amount : null,
            amount !== null && Number.isFinite(amount)
                ? t('shop.search.price_lte_chip', { amount: String(amount) })
                : undefined,
        );
        void runApply(next, response?.interpretation?.residual || query);
    }

    function onClear(): void {
        requestSeq.current += 1;
        setQuery('');
        setChips([]);
        setResponse(null);
        setStatus('idle');
        setErrorMessage(null);
        setUnavailableIds(new Set());
        setPriceCap('');
        setAvailability('');
    }

    async function onAddToCart(card: ShopSearchCard, rank: number): Promise<void> {
        if (!response?.query_id) {
            notify.error(t('shop.search.errors.missing_query'));
            return;
        }
        if (!hasExactVariant(card)) {
            notify.error(t('shop.search.errors.no_variant'));
            return;
        }

        const variantId = variantIdOf(card);
        setAddingId(variantId);

        try {
            // Revalidation live avant l'ajout (double filet côté client + serveur).
            const live = await revalidateVariant(initData, variantId, 1);
            if (!live.ok) {
                setUnavailableIds((prev) => new Set(prev).add(variantId));
                notify.error(t('shop.search.errors.variant_unavailable'));
                return;
            }

            const result = await addToCartFromSearch(initData, {
                shop_variant_id: variantId,
                query_id: response.query_id,
                shop_product_id: card.shop_product_id,
                quantity: 1,
            });

            void trackSearchEvent(initData, {
                query_id: response.query_id,
                event_type: 'add_to_cart',
                object_type: 'shop_product',
                object_id: card.shop_product_id,
                variant_id: variantId,
                rank,
                session_token: sessionToken,
            });

            const priceLabel = formatPrice(result.live?.price ?? live.price, card.currency || 'CHF', initData.locale || 'fr-CH');
            notify.success(
                result.message || t('shop.search.added_with_price', { price: priceLabel }),
            );
        } catch (err) {
            const code = err && typeof err === 'object' && 'code' in err
                ? String((err as { code?: string }).code || '')
                : '';
            if (code === 'gas_shop_search_variant_unavailable') {
                setUnavailableIds((prev) => new Set(prev).add(variantId));
                notify.error(t('shop.search.errors.variant_unavailable'));
            } else {
                notify.error(err instanceof Error ? err.message : t('shop.search.errors.add_failed'));
            }
        } finally {
            setAddingId(null);
        }
    }

    function onResultClick(card: ShopSearchCard, rank: number): void {
        if (!response?.query_id) return;
        void trackSearchEvent(initData, {
            query_id: response.query_id,
            event_type: 'click',
            object_type: 'shop_product',
            object_id: card.shop_product_id,
            variant_id: variantIdOf(card) || null,
            rank,
            session_token: sessionToken,
        });
    }

    const results = response?.results ?? [];

    return (
        <section className="gas-shop-search" aria-label={t('shop.search.title')}>
            <h2 className="gas-shop-search__title">{t('shop.search.title')}</h2>
            <p className="gas-shop-search__hint">{t('shop.search.hint')}</p>

            <div className="gas-shop-search__bar">
                <input
                    type="search"
                    className="gas-shop-search__input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('shop.search.placeholder')}
                    aria-label={t('shop.search.placeholder')}
                    data-testid="shop-search-input"
                />
                {query !== '' && (
                    <GasButton type="button" variant="secondary" onClick={onClear}>
                        {t('shop.search.clear')}
                    </GasButton>
                )}
            </div>

            {status === 'idle' && query.trim() === '' && (
                <div className="gas-shop-search__examples">
                    {examples.map((example) => (
                        <button
                            key={example}
                            type="button"
                            className="gas-shop-search__example"
                            onClick={() => setQuery(example)}
                        >
                            {example}
                        </button>
                    ))}
                </div>
            )}

            {chips.length > 0 && (
                <div className="gas-shop-search__chips" data-testid="shop-search-chips">
                    {chips.map((chip, index) => (
                        <span className="gas-shop-search__chip" key={`${chip.field}-${chip.op}-${chip.value}-${index}`}>
                            <span>{chip.label || `${chip.field}=${chip.value}`}</span>
                            <button
                                type="button"
                                className="gas-shop-search__chip-remove"
                                onClick={() => onRemoveChip(index)}
                                aria-label={t('shop.search.remove_chip', { label: chip.label || String(chip.value) })}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <div className="gas-shop-search__filters">
                <label className="gas-shop-search__filter-label" htmlFor="gas-shop-search-availability">
                    {t('shop.search.filters.availability')}
                </label>
                <select
                    id="gas-shop-search-availability"
                    className="gas-shop-search__filter-select"
                    value={availability}
                    onChange={(e) => onAvailabilityChange(e.target.value)}
                    data-testid="shop-search-availability"
                >
                    <option value="">{t('shop.search.filters.any')}</option>
                    <option value="in_stock">{t('shop.search.buckets.in_stock')}</option>
                    <option value="low_stock">{t('shop.search.buckets.low_stock')}</option>
                    <option value="backorder">{t('shop.search.buckets.backorder')}</option>
                </select>

                <label className="gas-shop-search__filter-label" htmlFor="gas-shop-search-price">
                    {t('shop.search.filters.price_max')}
                </label>
                <input
                    id="gas-shop-search-price"
                    type="number"
                    min={0}
                    step="0.05"
                    className="gas-shop-search__filter-input"
                    value={priceCap}
                    onChange={(e) => setPriceCap(e.target.value)}
                    placeholder="40"
                    data-testid="shop-search-price-cap"
                />
                <GasButton type="button" variant="secondary" onClick={onPriceCapApply}>
                    {t('shop.search.filters.apply_price')}
                </GasButton>
            </div>

            {status === 'loading' && (
                <div className="gas-shop-search__status" role="status" data-testid="shop-search-loading">
                    {t('shop.search.loading')}
                </div>
            )}

            {status === 'error' && errorMessage && (
                <div className="gas-shop-search__error" data-testid="shop-search-error">
                    <GasBannerAlert
                        type="danger"
                        title={t('shop.search.errors.title')}
                        message={errorMessage}
                    />
                </div>
            )}

            {status === 'denied' && (
                <GasBannerAlert
                    type="warning"
                    title={t('shop.search.errors.denied_title')}
                    message={errorMessage || t('shop.search.errors.denied')}
                />
            )}

            {status === 'empty' && (
                <GasEmptyState
                    title={t('shop.search.empty_title')}
                    message={t('shop.search.empty_description')}
                />
            )}

            {status === 'ready' && results.length > 0 && (
                <ul className="gas-shop-search__results" data-testid="shop-search-results">
                    {results.map((card, index) => {
                        const rank = index + 1;
                        const variantId = variantIdOf(card);
                        const bucket = (card.matched_variant?.availability_bucket
                            || card.availability_bucket
                            || 'unavailable') as AvailabilityBucket;
                        const isUnavailable = unavailableIds.has(variantId) || bucket === 'unavailable';
                        const price = card.matched_variant?.price ?? card.price_min ?? null;
                        const options = optionLabel(card.matched_variant?.option_keys);

                        return (
                            <li key={`${card.shop_product_id}-${variantId || rank}`} className="gas-shop-search__card">
                                <div className="gas-shop-search__card-media">
                                    {card.image ? (
                                        <img src={card.image} alt="" loading="lazy" />
                                    ) : null}
                                </div>
                                <div className="gas-shop-search__card-body">
                                    <h3 className="gas-shop-search__card-title">
                                        {card.url ? (
                                            <a
                                                href={card.url}
                                                onClick={() => onResultClick(card, rank)}
                                            >
                                                {card.title}
                                            </a>
                                        ) : (
                                            card.title
                                        )}
                                    </h3>

                                    <div className="gas-shop-search__card-meta">
                                        <span className="gas-shop-search__price">
                                            {formatPrice(price, card.currency || 'CHF', initData.locale || 'fr-CH')}
                                        </span>
                                        <GasBadge tone={bucketTone(bucket)}>
                                            {t(`shop.search.buckets.${bucket}`)}
                                        </GasBadge>
                                    </div>

                                    {options !== '' && (
                                        <div className="gas-shop-search__options" data-testid="shop-search-options">
                                            {t('shop.search.matched_variant')}: {options}
                                        </div>
                                    )}

                                    {card.match_reason ? (
                                        <div className="gas-shop-search__reason">{card.match_reason}</div>
                                    ) : null}

                                    {isUnavailable ? (
                                        <div className="gas-shop-search__unavailable" role="status">
                                            {t('shop.search.errors.variant_unavailable')}
                                        </div>
                                    ) : null}

                                    <div className="gas-shop-search__card-actions">
                                        <GasButton
                                            type="button"
                                            variant="primary"
                                            disabled={isUnavailable || !hasExactVariant(card) || addingId === variantId}
                                            onClick={() => void onAddToCart(card, rank)}
                                            testId="shop-search-add-to-cart"
                                        >
                                            {addingId === variantId
                                                ? t('shop.search.adding')
                                                : t('shop.search.add_to_cart')}
                                        </GasButton>
                                        {initData.cart_url ? (
                                            <a href={initData.cart_url} className="gas-button gas-button--secondary">
                                                {t('shop.search.view_cart')}
                                            </a>
                                        ) : null}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}
